// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: which Assistant DM messages belong to the Konvo panel's conversation. Matched by
// message id, never by clock: the panel keeps the ids of the messages it sent (from the
// send response), and an assistant message belongs to a turn when its snowflake id is
// after that request and no other user message (another tab, the DM itself) came in
// between. Pure, unit tested in panelThread.test.ts.

import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';

export interface ThreadMessage {
	id: string;
	author: {id: string};
}

export interface ThreadTurn<T> {
	message: T;
	role: 'user' | 'assistant';
}

export interface PanelThread<T> {
	/** Follow-ups and replies after the first request (the first request is shown as the request block). */
	turns: Array<ThreadTurn<T>>;
	/** Assistant messages answering the latest request. */
	latestReplies: Array<T>;
	/** Nothing else was said after the latest request: new assistant messages answer it. */
	answering: boolean;
}

export function panelThread<T extends ThreadMessage>(
	messages: ReadonlyArray<T>,
	requestIds: ReadonlyArray<string>,
	assistantId: string,
): PanelThread<T> {
	const first = requestIds[0];
	const latest = requestIds.at(-1);
	if (!first || !latest) return {turns: [], latestReplies: [], answering: false};
	const own = new Set(requestIds);
	// A request missing from the loaded window still starts its turn (a marker, not shown).
	const present = new Set(messages.map((message) => message.id));
	const markers = requestIds.filter((id) => !present.has(id)).map((id) => ({id, marker: true as const}));
	const sorted: Array<T | {id: string; marker: true}> = [...messages, ...markers].sort((a, b) =>
		SnowflakeUtils.compare(a.id, b.id),
	);
	const turns: Array<ThreadTurn<T>> = [];
	const latestReplies: Array<T> = [];
	let current: string | null = null;
	for (const entry of sorted) {
		if (SnowflakeUtils.compare(entry.id, first) < 0) continue;
		if ('marker' in entry) {
			current = entry.id;
			continue;
		}
		const message = entry;
		if (message.author.id !== assistantId) {
			// Ours: a new turn starts. Anyone else's message (another tab, typed in the DM):
			// what follows answers that, not the panel, until the panel asks again.
			current = own.has(message.id) ? message.id : null;
			if (current && current !== first) turns.push({message, role: 'user'});
			continue;
		}
		if (!current) continue;
		turns.push({message, role: 'assistant'});
		if (current === latest) latestReplies.push(message);
	}
	return {turns, latestReplies, answering: current === latest};
}
